/**
 * Web Vitals Monitor Component
 * Tracks Core Web Vitals and sends performance metrics to analytics
 */

import { useEffect } from 'react';
import { onCLS, onFCP, onFID, onINP, onLCP, onTTFB } from 'web-vitals';

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

interface WebVitalsConfig {
  enabled?: boolean;
  reportingEndpoint?: string;
  sampleRate?: number;
  debug?: boolean;
}

// Core Web Vitals thresholds based on Google's recommendations
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  FID: { good: 100, poor: 300 }, // First Input Delay (ms)
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (ms)
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
};

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private config: Required<WebVitalsConfig>;
  private reportTimer: NodeJS.Timeout | null = null;
  private observer: PerformanceObserver | null = null;

  constructor(config: WebVitalsConfig = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      reportingEndpoint: config.reportingEndpoint ?? '/api/v1/analytics/vitals',
      sampleRate: config.sampleRate ?? 1.0,
      debug: config.debug ?? false,
    };

    if (this.config.enabled) {
      this.initialize();
    }
  }

  private initialize() {
    // Set up performance observer for resource timing
    if ('PerformanceObserver' in window) {
      try {
        this.observer = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) {
            this.handlePerformanceEntry(entry);
          }
        });

        // Observe various performance entry types
        this.observer.observe({
          entryTypes: ['navigation', 'resource', 'paint', 'largest-contentful-paint', 'layout-shift', 'longtask'],
        });
      } catch (error) {
        console.warn('Failed to initialize PerformanceObserver:', error);
      }
    }

    // Set up periodic reporting
    this.reportTimer = setInterval(() => {
      this.sendMetrics();
    }, 30000); // Send metrics every 30 seconds

    // Send metrics on page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Send metrics on visibility change (mobile background)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handlePerformanceEntry(entry: PerformanceEntry) {
    // Track long tasks that block the main thread
    if (entry.entryType === 'longtask') {
      this.addMetric({
        name: 'LongTask',
        value: entry.duration,
        rating: entry.duration > 50 ? 'poor' : 'good',
        delta: 0,
        id: this.generateId(),
        navigationType: 'N/A',
      });
    }

    // Track resource loading performance
    if (entry.entryType === 'resource' && 'duration' in entry) {
      const resourceEntry = entry as PerformanceResourceTiming;

      // Only track slow resources
      if (resourceEntry.duration > 500) {
        this.addMetric({
          name: 'SlowResource',
          value: resourceEntry.duration,
          rating: resourceEntry.duration > 1000 ? 'poor' : 'needs-improvement',
          delta: 0,
          id: this.generateId(),
          navigationType: resourceEntry.name,
        });
      }
    }
  }

  public trackWebVital(metric: any) {
    // Determine rating based on thresholds
    const threshold = THRESHOLDS[metric.name as keyof typeof THRESHOLDS];
    let rating: 'good' | 'needs-improvement' | 'poor' = 'good';

    if (threshold) {
      if (metric.value >= threshold.poor) {
        rating = 'poor';
      } else if (metric.value >= threshold.good) {
        rating = 'needs-improvement';
      }
    }

    const performanceMetric: PerformanceMetric = {
      name: metric.name,
      value: Math.round(metric.value),
      rating,
      delta: Math.round(metric.delta),
      id: metric.id,
      navigationType: metric.navigationType || 'navigate',
    };

    this.addMetric(performanceMetric);

    if (this.config.debug) {
    }

    // Send immediately if it's a poor metric
    if (rating === 'poor') {
      this.sendMetrics();
    }
  }

  private addMetric(metric: PerformanceMetric) {
    // Apply sampling rate
    if (Math.random() > this.config.sampleRate) {
      return;
    }

    this.metrics.push(metric);

    // Limit metrics buffer size
    if (this.metrics.length > 100) {
      this.sendMetrics();
    }
  }

  private async sendMetrics(isUnload = false) {
    if (this.metrics.length === 0) return;

    const metricsToSend = [...this.metrics];
    this.metrics = [];

    const payload = {
      metrics: metricsToSend,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      connection: this.getConnectionInfo(),
      device: this.getDeviceInfo(),
      sessionId: this.getSessionId(),
    };

    try {
      if (isUnload && 'sendBeacon' in navigator) {
        // Use sendBeacon for reliable delivery on page unload
        navigator.sendBeacon(this.config.reportingEndpoint, JSON.stringify(payload));
      } else {
        // Use fetch for normal reporting
        await fetch(this.config.reportingEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (this.config.debug) {
      }
    } catch (error) {
      console.error('[Web Vitals] Failed to send metrics:', error);

      // Re-add metrics to buffer for retry
      this.metrics.unshift(...metricsToSend);
    }
  }

  private getConnectionInfo() {
    const connection =
      (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

    if (connection) {
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData,
      };
    }

    return null;
  }

  private getDeviceInfo() {
    return {
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window,
      platform: navigator.platform,
      memory: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
    };
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sigma08_session_id');

    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('sigma08_session_id', sessionId);
    }

    return sessionId;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public destroy() {
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Send any remaining metrics
    this.sendMetrics(true);

    // Clear metrics buffer
    this.metrics = [];
  }

  private handleBeforeUnload = () => {
    this.sendMetrics(true);
  };

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.sendMetrics();
    }
  };
}

// Singleton instance
let monitorInstance: PerformanceMonitor | null = null;

export const WebVitalsMonitor: React.FC<WebVitalsConfig> = config => {
  useEffect(() => {
    // Initialize monitor if not already done
    if (!monitorInstance) {
      monitorInstance = new PerformanceMonitor(config);
    }

    // Set up Web Vitals tracking
    const reportWebVital = (metric: any) => {
      monitorInstance?.trackWebVital(metric);
    };

    // Register Web Vitals handlers
    onCLS(reportWebVital);
    onFCP(reportWebVital);
    onFID(reportWebVital);
    onINP(reportWebVital);
    onLCP(reportWebVital);
    onTTFB(reportWebVital);

    // Additional custom metrics
    trackCustomMetrics();

    return () => {
      // Properly cleanup the monitor instance when component unmounts
      if (monitorInstance) {
        monitorInstance.destroy();
        monitorInstance = null;
      }
    };
  }, []);

  return null; // This component doesn't render anything
};

// Track custom performance metrics
function trackCustomMetrics() {
  // Time to Interactive (TTI)
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            const tti = performance.now();
            monitorInstance?.trackWebVital({
              name: 'TTI',
              value: tti,
              delta: 0,
              id: `tti-${Date.now()}`,
              navigationType: 'navigate',
            });
          }
        }
      });

      observer.observe({ entryTypes: ['paint'] });
    } catch (error) {
      console.warn('Failed to track TTI:', error);
    }
  }

  // JavaScript bundle size
  if (performance.getEntriesByType) {
    const resources = performance.getEntriesByType('resource');
    const jsResources = resources.filter(r => r.name.endsWith('.js'));

    const totalJsSize = jsResources.reduce((total, resource) => {
      return total + (resource as PerformanceResourceTiming).transferSize;
    }, 0);

    if (totalJsSize > 0) {
      monitorInstance?.trackWebVital({
        name: 'JSBundleSize',
        value: totalJsSize,
        delta: 0,
        id: `js-bundle-${Date.now()}`,
        navigationType: 'resource',
      });
    }
  }

  // Memory usage (if available)
  if ('memory' in performance) {
    const memory = (performance as any).memory;

    monitorInstance?.trackWebVital({
      name: 'MemoryUsage',
      value: memory.usedJSHeapSize,
      delta: 0,
      id: `memory-${Date.now()}`,
      navigationType: 'memory',
    });
  }
}

// Export utility functions for manual tracking
export const trackPerformanceMark = (markName: string) => {
  performance.mark(markName);

  monitorInstance?.trackWebVital({
    name: 'CustomMark',
    value: performance.now(),
    delta: 0,
    id: `mark-${Date.now()}`,
    navigationType: markName,
  });
};

export const trackPerformanceMeasure = (measureName: string, startMark: string, endMark?: string) => {
  try {
    performance.measure(measureName, startMark, endMark);

    const measures = performance.getEntriesByName(measureName, 'measure');
    const lastMeasure = measures[measures.length - 1];

    if (lastMeasure) {
      monitorInstance?.trackWebVital({
        name: 'CustomMeasure',
        value: lastMeasure.duration,
        delta: 0,
        id: `measure-${Date.now()}`,
        navigationType: measureName,
      });
    }
  } catch (error) {
    console.warn('Failed to track performance measure:', error);
  }
};

export default WebVitalsMonitor;
